import React from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FormDialog from '../common/FormDialog';

/**
 * The field-rendering Grid on its own, with no Dialog/FormDialog chrome -
 * shared by LedgerFormDialog (below) and live filter panels (e.g.
 * LedgerReports.js) that apply on change rather than behind a Save button.
 *
 * @param {Array}    fields  [{ key, label, type: 'text'|'number'|'date'|'select'|'switch'|'textarea'|'picker',
 *                             required?, options?: [{label,value}], helper?, min?, disabled?, xs?, md?, lg?,
 *                             picker?: Component (value=id, onChange(id)), pickerProps?: {...} }]
 * @param {object}   values
 * @param {Function} onChange (key, value) => void
 * @param {number}   spacing
 * @param {object}   sx
 */
export const LedgerFields = ({ fields = [], values = {}, onChange, spacing = 2, sx }) => {
  const set = (key) => (e) => {
    const target = e?.target;
    const value = target?.type === 'checkbox' ? target.checked : target?.value;
    onChange(key, value);
  };

  const renderField = (field) => {
    const value = values[field.key] ?? (field.type === 'switch' ? false : '');
    const common = {
      fullWidth: true,
      size: 'small',
      label: field.label,
      required: !!field.required,
      disabled: !!field.disabled,
      helperText: field.helper,
    };

    switch (field.type) {
      case 'number':
        return (
          <TextField
            {...common}
            type="number"
            value={value}
            onChange={set(field.key)}
            inputProps={{ min: field.min, step: field.step || 'any' }}
          />
        );
      case 'date':
        return (
          <TextField
            {...common}
            type="date"
            value={value || ''}
            onChange={set(field.key)}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'select':
        return (
          <FormControl fullWidth size="small" required={!!field.required} disabled={!!field.disabled}>
            <InputLabel>{field.label}</InputLabel>
            <Select value={value} label={field.label} onChange={set(field.key)}>
              {(field.options || []).map((o) => (
                <MenuItem key={String(o.value)} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'switch':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(value)}
                onChange={set(field.key)}
                disabled={!!field.disabled}
              />
            }
            label={field.label}
          />
        );
      case 'textarea':
        return (
          <TextField {...common} multiline rows={3} value={value} onChange={set(field.key)} />
        );
      case 'picker': {
        // Readable lookup: shows "CODE - Name", resolves to the numeric id.
        // The raw id is never displayed and never typed.
        const Picker = field.picker;
        if (!Picker) return null;
        return (
          <Picker
            label={field.label}
            value={value}
            onChange={(id) => onChange(field.key, id)}
            required={!!field.required}
            disabled={!!field.disabled}
            helper={field.helper}
            {...(field.pickerProps || {})}
          />
        );
      }
      case 'text':
      default:
        return <TextField {...common} value={value} onChange={set(field.key)} />;
    }
  };

  return (
    <Grid container spacing={spacing} sx={{ pt: 1, ...sx }}>
      {fields.map((field) => {
        const md = field.md || (field.type === 'switch' ? 12 : 6);
        const lg = field.lg || field.md || (field.type === 'switch' ? 12 : 5);
        return (
          <Grid key={field.key} size={{ xs: field.xs || 12, md, lg }}>
            {renderField(field)}
          </Grid>
        );
      })}
    </Grid>
  );
};

/**
 * Config-driven form dialog for ledger entities.
 *
 * @param {boolean}  open
 * @param {Function} onClose
 * @param {string}   title
 * @param {Array}    fields  see LedgerFields
 * @param {object}   values
 * @param {Function} onChange (key, value) => void
 * @param {Function} onSubmit
 * @param {boolean}  saving
 * @param {string}   maxWidth  Dialog width (default 'lg' - fields default to
 *                             two columns via md=6, so a wide dialog keeps
 *                             them from cramming; pass 'sm' for a dialog
 *                             with only one or two fields)
 */
const LedgerFormDialog = ({
  open,
  onClose,
  title,
  fields = [],
  values = {},
  onChange,
  onSubmit,
  saving = false,
  maxWidth = 'lg',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={title}
      onSubmit={onSubmit}
      saving={saving}
      isMobile={isMobile}
      maxWidth={maxWidth}
    >
      <LedgerFields fields={fields} values={values} onChange={onChange} />
    </FormDialog>
  );
};

export default LedgerFormDialog;
